import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Does a depression diagnosis mean I'll feel this way forever?",
    answer:
      "No. A diagnosis of major depressive disorder means you've had persistent low mood or loss of interest for at least two weeks along with other symptoms. It's a description of what you're experiencing now, not a prediction of your future. About one in eight teenagers has depression, and most who get treated go on to live full lives. Many describe getting treated as a teenager as one of the best things that ever happened to them.",
  },
  {
    question: "Why did I get depression as a teenager?",
    answer:
      "Depression runs in families and tends to first appear between 15 and 30. If a parent or sibling has it, your risk was higher from the start. Add the pressure of school, social life, identity, hormones, and a brain that's still developing, and the vulnerability can surface. You didn't cause it. Nobody did.",
  },
  {
    question:
      "What if therapy and medication don't work for my teen depression?",
    answer:
      "Some people don't get better on the first medication, or the second. That doesn't mean you're a hopeless case — it means your depression needs a different approach. Depression is also a circuitry problem: the region behind your forehead that regulates mood is underactive, and medication doesn't directly retrain it. Transcranial magnetic stimulation (TMS) does. Some standard TMS systems are FDA-cleared for teens 15 and older, and at 18, SAINT becomes an option.",
  },
  {
    question: "What is SAINT and when can I access it?",
    answer:
      "SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, accelerated form of TMS developed at Stanford. It uses an fMRI of your own brain to target treatment precisely, takes five days instead of six weeks, and in clinical trials produced remission in 79 percent of people who had already failed medication, with an average time to remission of 2.6 days. It's cleared for adults 18 and older. You may never need it, but knowing it exists changes what 'treatment-resistant' means.",
  },
  {
    question: "What can I do right now to help myself?",
    answer:
      "Take the treatment seriously — go to appointments, take medication as prescribed, and tell your doctor the truth. Protect your sleep, since depression and sleep loss feed each other. Move, even a little — a walk counts. Stay off alcohol and drugs, which reliably make depression worse. Don't disappear — isolation is both a symptom and a fuel. Let one person in. And if you ever think about hurting yourself, tell someone immediately or call or text 988.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "Teen Depression: How to Tell the Difference Between a Hard Year and a Real Illness",
    href: "/teen-depression-hard-year-or-real-illness",
    description:
      "How to tell the difference between normal teenage mood swings and clinical depression.",
  },
  {
    title:
      "You Just Started Therapy at 27. That's Not Late. That's the Beginning.",
    href: "/you-just-started-therapy-at-27-thats-not-late",
    description:
      "Why starting treatment later than you'd hoped is still starting, and what to expect in the first weeks.",
  },
  {
    title:
      "Is My Child Depressed? What Parents Should Know, and Why Your Own Depression Is Part of the Answer",
    href: "/is-my-child-depressed-what-parents-should-know",
    description:
      "What childhood depression looks like, when to seek help, and what your own mental health has to do with it.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function TeenDiagnosedDepression() {
  return (
    <DepressionContentPage
      seo={{
        title: "Diagnosed With Depression as a Teenager? Here's What It Means",
        description:
          "A depression diagnosis as a teenager is a starting point, not a verdict. What it means, why it happened, what treatment looks like, and what to do when the first steps aren't enough.",
        canonical: "/diagnosed-with-depression-as-a-teenager",
        keywords:
          "teenager depression diagnosis, major depressive disorder teen, teen depression treatment, treatment-resistant depression teenager, SAINT TMS young adult, TMS for teens, cognitive behavioral therapy teen, depression not a phase",
      }}
      badge="For Teens & Families"
      heroTitle="You're a Teenager and You Were Just Diagnosed With Depression. Here's What That Actually Means."
      heroSubtitle="A diagnosis is not a verdict. It's a starting point. And the starting point for teen depression is a lot better than it used to be. Here's what the diagnosis means, why it happened, what treatment looks like, and what to do when the first steps aren't enough."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                Somebody sat across from you, asked a lot of questions, and told
                you that you have major depressive disorder. Maybe your parents
                were in the room. Maybe you'd been telling everyone you were
                fine for a year before that appointment. Maybe part of you is
                relieved, because now the thing has a name, and part of you is
                terrified, because now the thing has a name.
              </p>
              <p>
                Here's what nobody may have said clearly enough: a diagnosis is
                not a verdict. It's a starting point. And the starting point for
                teen depression is a lot better than it used to be.
              </p>
            </div>
          ),
        },
        {
          heading: "What the diagnosis means, and what it doesn't",
          body: (
            <div className="space-y-5">
              <p>
                Major depressive disorder means that for at least two weeks,
                most of the day, nearly every day, you've had a low mood or lost
                interest in things, plus several other symptoms: sleep changes,
                appetite changes, no energy, trouble concentrating, feeling
                worthless, maybe thoughts you'd never say out loud. It means the
                thing you've been experiencing is a real medical condition, not
                a personality flaw and not a phase.
              </p>
              <p>
                It does not mean you're broken. It does not mean you'll feel
                this way forever. It does not mean you can't go to college, have
                relationships, or do the things you were planning. About one in
                eight teenagers has depression. Most of the people who treat it
                well go on to live full lives, and some of them will tell you
                that getting treated as a teenager was one of the best things
                that ever happened to them.
              </p>
            </div>
          ),
        },
        {
          heading: "Why it happened to you",
          body: (
            <div className="space-y-5">
              <p>
                Depression runs in families, and it tends to show up first
                between 15 and 30. If a parent or sibling has it, your risk was
                higher from the start. Add the pressure of school, social life,
                identity, hormones, and a brain that's still developing, and the
                vulnerability can surface. You didn't cause it. Nobody did.
              </p>
            </div>
          ),
        },
        {
          heading: "What treatment looks like",
          body: (
            <div className="space-y-5">
              <p>
                The first steps are therapy, often cognitive behavioral therapy,
                which teaches you to recognize and change the thinking patterns
                depression runs on. If your depression is moderate to severe, a
                psychiatrist may add a medication. Research shows the
                combination works best, with roughly three in four teens
                improving. Medication for teens requires careful monitoring, and
                your doctor will explain why.
              </p>
              <p>
                For most teenagers, this is enough. Not overnight, but within
                months, the fog lifts.
              </p>
            </div>
          ),
        },
        {
          heading: "What if it isn't enough?",
          body: (
            <div className="space-y-5">
              <p>
                Some people don't get better on the first medication, or the
                second. If that happens, it doesn't mean you're a hopeless case.
                It means your depression is the kind that needs a different
                approach, and different approaches exist.
              </p>
              <p>
                Depression isn't only about brain chemistry. It's also about
                brain circuitry: the region behind your forehead that regulates
                mood is underactive, and medication doesn't directly retrain it.
                Transcranial magnetic stimulation (TMS) does, using magnetic
                pulses to stimulate that region. Some standard TMS systems are
                FDA-cleared for teens 15 and older whose depression hasn't
                responded to medication.
              </p>
              <p>
                And when you turn 18, another door opens. SAINT (Stanford
                Accelerated Intelligent Neuromodulation Therapy) is an
                FDA-cleared, accelerated form of TMS developed at Stanford. It
                uses an fMRI of your own brain to target treatment precisely,
                takes five days instead of six weeks, and in clinical trials
                produced remission in 79 percent of people who had already
                failed medication, with an average time to remission of 2.6
                days. It's cleared for adults 18 and older.
              </p>
              <p>
                The point isn't that you'll need it. The point is that the road
                doesn't end where you are.
              </p>
            </div>
          ),
        },
        {
          heading: "Things you can do now",
          body: (
            <div className="space-y-3">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Take the treatment seriously. Go to the appointments. Take the
                  medication as prescribed. Tell your doctor the truth.
                </li>
                <li>
                  Protect your sleep. Depression and sleep loss feed each other.
                </li>
                <li>Move, even a little. A walk counts.</li>
                <li>
                  Stay off alcohol and drugs. They make depression worse,
                  reliably.
                </li>
                <li>
                  Don't disappear. Isolation is a symptom and a fuel. Let one
                  person in.
                </li>
                <li>
                  If you ever think about hurting yourself, tell someone
                  immediately, or call or text 988.
                </li>
              </ul>
            </div>
          ),
        },
        {
          heading: "For your parents",
          body: (
            <div className="space-y-5">
              <p>
                Your teen's diagnosis is not a failure of parenting. It's a
                treatable illness that showed up at the age it usually shows up.
                The best thing you can do is take it seriously without
                panicking, keep the lines open, and make sure treatment actually
                happens. And if your teenager reaches 18 still struggling after
                trying medication, know that SAINT exists, and that Neuro
                Wellness TMS Centers of America is a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>
                .
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                Follow the plan your doctor gave you. Give it a real chance. And
                if you or your parents want to understand what the next options
                look like, or your teen is now 18 and medication hasn't worked,
                call us.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We'll tell you honestly what
                fits.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
